import { useCallback, useEffect, useState } from 'react'
import { Icon } from '@carrent/ui'
import { kbApi } from '@carrent/api'
import { DiffView } from './DiffView'

const fmt = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  })
}

/**
 * История версий.
 *
 * Показываются опубликованные версии: черновики и отклонённые правки
 * историей статьи не являются. Выбранная версия сравнивается с текущей,
 * поэтому «что изменилось с тех пор» читается сразу.
 *
 * Откат делает новую версию поверх, а не стирает промежуточные:
 * история должна оставаться правдой о том, что происходило.
 */
export function HistoryPanel({ page, level, onClose, onReverted }) {
  const [revisions, setRevisions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selected, setSelected] = useState(null)
  const [body, setBody] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    kbApi.revisions(page.id)
      .then((r) => {
        const published = (r.data?.data || []).filter((x) => x.status === 'approved')
        setRevisions(published)
        setError('')
      })
      .catch((e) => setError(e.response?.data?.message || 'Не удалось загрузить историю'))
      .finally(() => setLoading(false))
  }, [page.id])

  useEffect(() => { load() }, [load])

  const open = async (rev) => {
    setSelected(rev)
    try {
      const r = await kbApi.revision(page.id, rev.id)
      setBody(r.data?.data?.body_md || '')
    } catch (e) {
      setError(e.response?.data?.message || 'Не удалось открыть версию')
    }
  }

  const revert = async () => {
    if (!confirm(`Вернуть версию №${selected.number}? Она станет текущей, история сохранится.`)) {
      return
    }
    setBusy(true)
    try {
      await kbApi.revert(page.id, selected.id)
      onReverted()
    } catch (e) {
      setError(e.response?.data?.message || 'Не удалось вернуть версию')
      setBusy(false)
    }
  }

  const isCurrent = selected && selected.number === page.revision_number

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="История версий">
      <div className="modal modal--wide">
        <header className="modal__head">
          <h2 className="modal__title">История: {page.title}</h2>
          <button type="button" className="modal__close" onClick={onClose} aria-label="Закрыть">
            <Icon name="close" size={20} />
          </button>
        </header>

        <div className="modal__body history">
          {loading && <div className="groups__loading"><div className="spinner" /></div>}
          {error && <p className="field__error">{error}</p>}

          {!loading && revisions.length === 0 && (
            <p className="groups__empty">Статья ещё ни разу не публиковалась.</p>
          )}

          {revisions.length > 0 && (
            <div className="history__panes">
              <ul className="history__list">
                {revisions.map((r) => (
                  <li key={r.id}>
                    <button
                      type="button"
                      className={`history__item${selected?.id === r.id ? ' is-active' : ''}`}
                      onClick={() => open(r)}
                    >
                      <span className="history__num">Версия {r.number}</span>
                      <span className="history__date">{fmt(r.reviewed_at || r.created_at)}</span>
                      {r.number === page.revision_number && (
                        <span className="history__current">текущая</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>

              <div className="history__detail">
                {!selected && (
                  <p className="groups__empty">Выберите версию, чтобы увидеть отличия.</p>
                )}
                {selected && (
                  <>
                    <DiffView
                      before={body}
                      after={page.body_md || ''}
                      beforeLabel={`Версия ${selected.number}`}
                      afterLabel="Текущая"
                    />
                    {level === 'admin' && !isCurrent && (
                      <button type="button" className="btn btn--secondary" disabled={busy}
                        onClick={revert}>
                        Вернуть версию {selected.number}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
